import { ref, computed, watch } from '@vue/composition-api'
import analytics from '@/@core/utils/analytics'
import { apiToastSuccess } from '@/@core/utils/toast'
import { formatNumber } from '@/@core/utils/utils'
import constants, { DOWNLOAD_TYPES, FILE_FORMATS } from '@/constants'
import store from '@/store'
import { DOWNLOAD_COLLECTION, GET_COLLECTION_LIST } from '@/store/modules/collection.module'
import { UPLOAD_DOCUMENTS } from '@/store/modules/common.module'
import {
  IMPORT_WISHLIST,
  SET_SELECTION_NAME,
  isEnabledHintPopup,
} from '@/store/modules/shop.module'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import { SHOW_DOWNLOADS_NAV_INDICATOR } from '@/store/modules/notification.module'
import { debounce } from 'lodash'

const { FILE_TYPES } = c

export function useCreateSelection(fetchWishlistsFn) {
  const selectedCollection = ref({ id: '', name: '' })
  const selectionName = ref('')
  const collections = ref([])
  const productsFile = ref(null)
  const isImporting = ref(false)
  const isExporting = ref(false)
  const selectedImportOption = ref('standard')
  const isImportUsingUniqueId = ref(false)
  const isImportUsingStyleCode = ref(false)

  const userData = computed(() => store.getters.profile)

  const isValid = computed(() => !!selectedCollection.value?.id && selectionName.value)

  // Watchers
  function applyImportOption(option) {
    isImportUsingStyleCode.value = option === 'styleCode'
    isImportUsingUniqueId.value = option === 'barcode'
  }

  watch(selectedImportOption, newOption => {
    analytics.track(constants.TRACKS.ACTIONS.SELECTIONS.BRAND_CHANGES_ADD_SELECTION_IMPORT_TYPE, {
      newImportType: newOption,
    })
    applyImportOption(newOption)
  })

  watch(productsFile, file => {
    if (file) {
      analytics.track(constants.TRACKS.ACTIONS.SELECTIONS.BRAND_SELECTS_ADD_SELECTION_IMPORT_FILE, {
        fileName: file.name,
      })
    }
  })

  // Methods
  function buildDefault() {
    return { id: '', name: '' }
  }

  function getCollections() {
    const params = {
      brandId: userData.value?._id,
    }
    store
      .dispatch(GET_COLLECTION_LIST, params)
      .then(res => {
        collections.value = res.data.data.collections
      })
      .catch(() => { })
  }

  const debouncedTrackSelectionName = debounce(name => {
    analytics.track(constants.TRACKS.ACTIONS.SELECTIONS.BRAND_ENTERS_ADD_SELECTION_NAME, {
      selectionName: name,
    })
  }, 500)

  function onInputSelectionName(text = '') {
    selectionName.value = text
    store.commit(SET_SELECTION_NAME, text)
    debouncedTrackSelectionName(text)
  }

  function onClickCollection(collection) {
    selectedCollection.value.id = collection._id
    selectedCollection.value.name = collection.name
    analytics.track(
      constants.TRACKS.ACTIONS.BRAND_CHOOSES_COLLECTION_FOR_SELECTION,
      { 'Created By': new Date(), 'Collection Name': collection.name },
    )
  }

  function downloadCatalogue() {
    analytics.track(constants.TRACKS.ACTIONS.SELECTIONS.BRAND_CLICKS_ADD_SELECTION_EXPORT_BUTTON, {
      selectedCollection: selectedCollection.value,
    })
    isExporting.value = true
    const payload = {
      respectiveId: selectedCollection.value.id,
      fileType: FILE_FORMATS.XLSX,
      downloadType: DOWNLOAD_TYPES.COLLECTION_EXPORT.VALUE,
    }
    store
      .dispatch(DOWNLOAD_COLLECTION, { payload })
      .then(res => {
        apiToastSuccess(res?.data?.message || 'Download created successfully')
        store.commit(SHOW_DOWNLOADS_NAV_INDICATOR)
      })
      .catch(() => {
        analytics.track(constants.TRACKS.ACTIONS.SELECTIONS.BRAND_ADD_SELECTION_EXPORT_COLLECTION_FAILED, {
          selectedCollection: selectedCollection.value,
        })
      })
      .finally(() => {
        isExporting.value = false
      })
  }

  function getImportTrackingData() {
    return {
      selectionName: selectionName.value,
      selectedCollection: selectedCollection.value,
      fileName: productsFile.value?.name,
    }
  }

  async function buildImportPayload() {
    const payload = {
      collectionId: selectedCollection.value.id,
      productsFile: await store.dispatch(UPLOAD_DOCUMENTS, {
        file: productsFile.value,
        fileType: FILE_TYPES.WISHLIST_IMPORT,
      }),
    }
    if (selectionName.value) {
      payload.name = selectionName.value
    }
    return payload
  }

  function buildImportParams() {
    const params = {}
    if (isImportUsingUniqueId.value) {
      params.isImportUsingUniqueId = true
    }
    if (isImportUsingStyleCode.value) {
      params.isImportUsingStyleCode = true
    }
    return params
  }

  function handleImportSuccess(message, hideModalFn) {
    apiToastSuccess(message)
    analytics.track(
      constants.TRACKS.ACTIONS.SELECTIONS.BRAND_ADD_SELECTION_IMPORT_SUCCESS,
      getImportTrackingData(),
    )
    fetchWishlistsFn()
    if (hideModalFn) hideModalFn()
    selectionName.value = ''
    selectedCollection.value = buildDefault()
  }

  function handleImportError() {
    analytics.track(
      constants.TRACKS.ACTIONS.SELECTIONS.BRAND_ADD_SELECTION_IMPORT_FAILED,
      getImportTrackingData(),
    )
  }

  async function importSelection(hideModalFn) {
    analytics.track(
      constants.TRACKS.ACTIONS.SELECTIONS.BRAND_CLICKS_ADD_SELECTION_IMPORT_BUTTON,
      getImportTrackingData(),
    )
    isImporting.value = true
    try {
      const payload = await buildImportPayload()
      const params = buildImportParams()
      const res = await store.dispatch(IMPORT_WISHLIST, { payload, params })
      handleImportSuccess(res.data.message, hideModalFn)
    } catch (err) {
      handleImportError(err)
    } finally {
      isImporting.value = false
    }
  }

  function onHideImportModal() {
    if (!isImporting.value) {
      analytics.track(constants.TRACKS.ACTIONS.SELECTIONS.BRAND_CLOSES_ADD_SELECTION_MODAL, {
        step: 2,
        selectionName: selectionName.value,
        selectedCollection: selectedCollection.value,
        fileName: productsFile.value?.name,
      })
    }
    productsFile.value = null
    isImportUsingUniqueId.value = false
  }

  function resetSelection() {
    selectedCollection.value = buildDefault()
    selectionName.value = ''
  }

  // Init
  function initCreateSelection() {
    store.commit(SET_SELECTION_NAME, '')
    getCollections()
  }

  return {
    // State
    selectedCollection,
    selectionName,
    collections,
    productsFile,
    isImporting,
    isExporting,
    selectedImportOption,
    isImportUsingUniqueId,
    isImportUsingStyleCode,

    // Computed
    userData,
    isValid,

    // Methods
    buildDefault,
    getCollections,
    onInputSelectionName,
    onClickCollection,
    downloadCatalogue,
    importSelection,
    onHideImportModal,
    resetSelection,
    initCreateSelection,

    // Constants
    formatNumber,
    isEnabledHintPopup,
  }
}
